import { fileRepository } from '~/repositories/fileRepository';

const FRAME_WIDTH_PX = 1024;
const FRAME_HEIGHT_PX = 1400;
const ASSET_TIMEOUT_MS = 5000;
const FRAME_CLEANUP_MS = 60000;

const collectDocumentStyles = () => {
  let styles = '';
  const linkTags = document.getElementsByTagName('link');
  for (let i = 0; i < linkTags.length; i++) {
    if (linkTags[i].rel === 'stylesheet') {
      styles += linkTags[i].outerHTML;
    }
  }
  const styleTags = document.getElementsByTagName('style');
  for (let i = 0; i < styleTags.length; i++) {
    styles += styleTags[i].outerHTML;
  }
  return styles;
};

const withTimeout = (promise: Promise<unknown>, timeout: number) =>
  Promise.race([promise, new Promise<void>((resolve) => setTimeout(resolve, timeout))]);

const waitForFrameAssets = (frameWindow: Window) => {
  const frameDocument = frameWindow.document;
  const loaded =
    frameDocument.readyState === 'complete'
      ? Promise.resolve()
      : new Promise<void>((resolve) => frameWindow.addEventListener('load', () => resolve(), { once: true }));

  return withTimeout(
    loaded.then(() => {
      const pendingImages = Array.from(frameDocument.images)
        .filter((image) => !image.complete)
        .map(
          (image) =>
            new Promise<void>((resolve) => {
              image.addEventListener('load', () => resolve(), { once: true });
              image.addEventListener('error', () => resolve(), { once: true });
            })
        );
      return Promise.all([frameDocument.fonts.ready, ...pendingImages]);
    }),
    ASSET_TIMEOUT_MS
  );
};

export function printDocument(printContainer: HTMLDivElement, printWrapClass?: string, printHeader?: Element, extraStyle = '') {
  const content = printContainer?.innerHTML;
  if (!content) return;

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.left = '-10000px';
  iframe.style.top = '0';
  iframe.style.width = `${FRAME_WIDTH_PX}px`;
  iframe.style.height = `${FRAME_HEIGHT_PX}px`;
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  const frameWindow = iframe.contentWindow;
  const frameDocument = iframe.contentDocument;
  if (!frameWindow || !frameDocument) {
    iframe.remove();
    return;
  }

  let isRemoved = false;
  const removeFrame = () => {
    if (isRemoved) return;
    isRemoved = true;
    iframe.remove();
  };

  frameDocument.open();
  frameDocument.write(
    [
      '<!DOCTYPE html><html><head><meta charset="utf-8">',
      collectDocumentStyles(),
      '<style type="text/css">',
      '[data-slot="report"] [data-slot="label"] { min-width: 1px !important; }',
      'body { margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }',
      '</style></head><body>',
      `<div class="${printWrapClass ?? ''}" style="${extraStyle}">${printHeader?.innerHTML || ''} ${content}</div>`,
      '</body></html>',
    ].join('')
  );
  frameDocument.close();

  waitForFrameAssets(frameWindow)
    .then(() => {
      frameWindow.addEventListener('afterprint', removeFrame, { once: true });
      frameWindow.focus();
      frameWindow.print();
      setTimeout(removeFrame, FRAME_CLEANUP_MS);
    })
    .catch((error) => {
      console.error('Error printing document', error);
      removeFrame();
    });
}

export const printUrl = async (fileUrl?: string): Promise<HTMLIFrameElement | undefined> => {
  if (!fileUrl) {
    return;
  }
  const response = await fileRepository.fetchFileResponse(fileUrl);
  const pdfBlob = await response.blob();
  return printFileBlob(pdfBlob);
};

export const printFileBlob = (blob?: Blob): HTMLIFrameElement | undefined => {
  if (!blob) {
    return;
  }
  try {
    const blobUrl = URL.createObjectURL(blob);
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    document.body.appendChild(iframe);
    iframe.src = blobUrl;
    iframe.onload = function () {
      try {
        iframe.contentWindow?.print();
      } catch (error) {
        console.error('Error printing document', error);
      }
    };
    return iframe;
  } catch (error) {
    console.error('Error printing document', error);
  }
};

export const resetPrintSystem = (iFrame?: HTMLIFrameElement) => {
  if (iFrame && document.body.contains(iFrame)) {
    document.body.removeChild(iFrame);
  }
  if (iFrame?.src) {
    URL.revokeObjectURL(iFrame.src);
  }
};
